import {
  LocationType,
  SimilarityFeature,
  SimilarityScoreWeights,
  SimilarityScoreWeightsForLocationType,
  defaultSimilarityScoreWeights,
  schSimilarityFeatures,
} from '@aloft/toolkit-shared';
import { Button, Flex, Icon, Modal, Typography } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';

import { LoadingScreenSamInline } from '../../LoadingScreen/LoadingScreenSamInline.tsx';

const FEATURE_LABELS: Record<SimilarityFeature, string> = {
  gla: 'GLA',
  proximityMiles: 'Proximity',
  actualAge: 'Actual Age',
  quality: 'Quality',
  condition: 'Condition',
  lotSize: 'Site Size',
  saleDate: 'Sale Date',
  bedroomCount: 'Beds',
  fullBathCount: 'Baths Full',
  halfBathCount: 'Baths Half',
  garage: 'Garage',
  basement: 'Basement',
  pool: 'Pool',
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  locationType: LocationType;
  weights?: SimilarityScoreWeights;
  isLoadingWeights: boolean;
  save: (weights: SimilarityScoreWeights, saveAsDefault?: boolean) => Promise<void>;
  isSaving: boolean;
};

export const SimilarityScoreWeightingModal = ({
  isOpen,
  onClose,
  locationType,
  weights,
  isLoadingWeights,
  save,
  isSaving,
}: Props) => {
  const [localWeights, setLocalWeights] = useState<SimilarityScoreWeightsForLocationType | null>(
    null,
  );

  // Initialize (or reset) local state whenever the modal opens or remote weights change
  useEffect(() => {
    if (isOpen && weights) {
      setLocalWeights({
        ...(weights[locationType] ?? defaultSimilarityScoreWeights[locationType]),
      });
    }
  }, [isOpen, weights, locationType]);

  const total = localWeights
    ? schSimilarityFeatures.options.reduce((sum, f) => sum + (localWeights[f] ?? 0), 0)
    : 0;

  const isValid = total === 100;

  const handleChange = (feature: SimilarityFeature, value: string) => {
    const num = parseInt(value, 10);
    if (Number.isNaN(num)) return;
    setLocalWeights((prev) => (prev ? { ...prev, [feature]: num } : prev));
  };

  const buildFullWeights = (): SimilarityScoreWeights => {
    if (!weights || !localWeights) throw new Error('Weights not loaded');
    return {
      ...weights,
      [locationType]: localWeights,
    } as SimilarityScoreWeights;
  };

  const handleClose = async () => {
    if (!isValid || !localWeights) return;
    await save(buildFullWeights(), false);
    onClose();
  };

  const handleSetAsDefault = async () => {
    if (!isValid || !localWeights) return;
    await save(buildFullWeights(), true);
    onClose();
  };

  const isDisabled = !isValid || isSaving || isLoadingWeights || !localWeights;

  const renderField = (feature: SimilarityFeature) => (
    <Flex key={feature} alignItems="center" gap="1rem">
      <div style={{ width: '70px', flexShrink: 0 }}>
        <Typography variant="body1" color="textPrimary">
          {FEATURE_LABELS[feature]}
        </Typography>
      </div>
      <Flex alignItems="center" gap="4px">
        <input
          type="number"
          min={0}
          max={100}
          value={localWeights?.[feature] ?? 0}
          onChange={(e) => handleChange(feature, e.target.value)}
          style={{
            width: '52px',
            padding: '8px',
            border: '1px solid #CDD3D7',
            borderRadius: '4px',
            fontSize: '14px',
            fontFamily: 'Lato, sans-serif',
            color: '#072138',
            textAlign: 'right',
          }}
        />
        <Typography variant="body1" color="textTertiary">
          %
        </Typography>
      </Flex>
    </Flex>
  );

  const renderContent = () => {
    if (isLoadingWeights || !localWeights) {
      return (
        <Flex justifyContent="center" alignItems="center" padding="2rem">
          <LoadingScreenSamInline what="weights" />
        </Flex>
      );
    }

    return (
      <>
        <Typography
          variant="body2"
          color="textSecondary"
          __dangerDoNotUseSx={{ marginBottom: '1rem' }}
        >
          Similarity Score is calculated based on the contribution percentage of each listed
          feature.
        </Typography>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.5rem 3rem',
            marginBottom: '1rem',
          }}
        >
          {schSimilarityFeatures.options.map(renderField)}
        </div>

        <Flex justifyContent="flex-end" alignItems="center" flexDirection="column" gap="4px">
          <Flex alignItems="center" gap="0.5rem">
            <Typography
              variant="h2"
              color={isValid ? 'textPrimary' : 'alertError'}
              __dangerDoNotUseSx={{ whiteSpace: 'nowrap' }}
            >
              Total: {total}%
            </Typography>
            {!isValid && <Icon icon="error-warning" size="sm" color="alertError" />}
          </Flex>
          {/* Always reserve the height of the helper text to prevent layout shift */}
          <Typography
            variant="body2"
            color="textTertiary"
            __dangerDoNotUseSx={{ visibility: isValid ? 'hidden' : 'visible' }}
          >
            Must equal 100%
          </Typography>
        </Flex>
      </>
    );
  };

  return (
    <Modal
      title="Similarity Score Weighting"
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      showCloseButton={false}
    >
      <div style={{ padding: '0.5rem 0' }}>{renderContent()}</div>

      <Flex justifyContent="flex-end" gap="1rem" marginTop="1rem" alignItems="center">
        <Button
          variant="inline"
          text={`Set as ${locationType} Default for New Orders`}
          onClick={handleSetAsDefault}
          disabled={isDisabled}
        />
        <Button
          variant="primary"
          text="Close"
          onClick={handleClose}
          disabled={isDisabled}
          __dangerDoNotUseSx={{ paddingInline: '20px' }}
        />
      </Flex>
    </Modal>
  );
};
